import React from "react";
import { Network } from "lucide-react";
import type { ContainerSelected } from "components/Environment/shared/BuildTypes";
import NetworkAccessConfiguration from "../components/NetworkAccessConfiguration";
import type { FirewallRule, NetworkService } from "../networkServices";
import { SectionShell } from "../layout";
import type { RuntimeType, SectionControlProps } from "../types";

const NetworkCollapsedPreview: React.FC<{
  services: NetworkService[];
  firewallRules: FirewallRule[];
}> = ({ services, firewallRules }) => {
  const networkPillClass =
    "inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/70 px-2 py-0.5 text-[11px] leading-4 text-primary";
  const networkPillDividerClass = "h-3 w-px bg-zinc-700";
  const allowAllFirewallRules = firewallRules.filter(
    (rule) => rule.allowedIPs === "all" || rule.allowedIPs === "0.0.0.0/0",
  );
  const userIpFirewallRules = firewallRules.filter(
    (rule) => rule.allowedIPs === "user-ip",
  );
  const hasVisibleFirewallRules =
    allowAllFirewallRules.length > 0 || userIpFirewallRules.length > 0;

  return (
    <div className="mt-3 grid gap-3 text-sm">
      {services.length > 0 && (
        <div>
          <p className="mb-1.5 font-medium text-secondary">Secure links</p>
          <ul className="flex flex-wrap gap-2">
            {services.map((service) => (
              <li key={service.id} className={networkPillClass}>
                <span className="font-medium">{service.name}</span>
                <span className={networkPillDividerClass} aria-hidden="true" />
                <span className="font-mono text-secondary">{service.port}</span>
                {service.isCTA && (
                  <>
                    <span
                      className={networkPillDividerClass}
                      aria-hidden="true"
                    />
                    <span className="font-medium text-secondary">CTA</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasVisibleFirewallRules && (
        <div>
          <p className="mb-1.5 font-medium text-secondary">TCP/UDP ports</p>
          <div className="space-y-2">
            {allowAllFirewallRules.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="shrink-0 text-xs font-medium text-secondary">
                  Allow all IPs:
                </span>
                <ul className="flex flex-wrap gap-2">
                  {allowAllFirewallRules.map((rule) => (
                    <li key={rule.id} className={networkPillClass}>
                      <span className="font-mono">{rule.ports}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {userIpFirewallRules.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="shrink-0 text-xs font-medium text-secondary">
                  Only your IPs:
                </span>
                <ul className="flex flex-wrap gap-2">
                  {userIpFirewallRules.map((rule) => (
                    <li key={rule.id} className={networkPillClass}>
                      <span className="font-mono">{rule.ports}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const NetworkSection: React.FC<
  SectionControlProps & {
    summary?: string;
    hasError: boolean;
    services: NetworkService[];
    onServicesChange: (services: NetworkService[]) => void;
    runtimeType: RuntimeType;
    resolvedContainer?: ContainerSelected;
    firewallRules: FirewallRule[];
    onFirewallRulesChange: (rules: FirewallRule[]) => void;
    configurationError?: string;
  }
> = ({
  summary,
  hasError,
  services,
  onServicesChange,
  runtimeType,
  resolvedContainer,
  firewallRules,
  onFirewallRulesChange,
  configurationError,
  status,
  ...sectionProps
}) => (
  <SectionShell
    id="network"
    title="Network"
    summary={summary}
    collapsedPreview={
      services.length > 0 || firewallRules.length > 0 ? (
        <NetworkCollapsedPreview
          services={services}
          firewallRules={firewallRules}
        />
      ) : undefined
    }
    icon={<Network className="h-5 w-5" />}
    status={hasError ? "error" : status}
    {...sectionProps}
  >
    <NetworkAccessConfiguration
      services={services}
      onServicesChange={onServicesChange}
      runtimeType={runtimeType || "VM"}
      container={
        resolvedContainer || {
          vmBuild: {
            forceJupyterInstall: true,
          },
        }
      }
      firewallRules={firewallRules}
      onFirewallRulesChange={onFirewallRulesChange}
      configurationError={configurationError}
    />
  </SectionShell>
);
